import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { HandHeart, Feather, ChatCircleText, ShieldCheck, Waveform, CheckCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1750064139819-da3bf362e7b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxzaWxrJTIwcm9iZSUyMHNsZWVwd2VhcnxlbnwwfHx8fDE3ODMwODExNTZ8MA&ixlib=rb-4.1.0&q=85";
const EDIT = "https://images.unsplash.com/photo-1766056278976-87f269e3b69c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxzaWxrJTIwcm9iZSUyMHNsZWVwd2VhcnxlbnwwfHx8fDE3ODMwODExNTZ8MA&ixlib=rb-4.1.0&q=85";

const PRINCIPLES = [
  { icon: HandHeart, title: "Touch, not performance", body: "Every session focuses on presence, not pleasure. Reintroducing closeness without expectation." },
  { icon: ChatCircleText, title: "Communication first", body: "Structured prompts that make it easier to say what has, for too long, gone unsaid." },
  { icon: Feather, title: "Non-explicit, always", body: "Clinically-grounded material designed for real couples. Nothing suggestive, nothing prescriptive." },
  { icon: ShieldCheck, title: "Private and paced", body: "You move at the speed of your relationship. No timelines, no comparisons, no watching eyes." },
];

const STEPS = [
  { n: "01", title: "Set the room", body: "Lower the lights. Silence the phones. Put on the piece. This is a fifteen-minute reset before anything begins." },
  { n: "02", title: "Play the audio", body: "Start the 15–20 minute audio session together. Let the therapist's voice guide you — no need to remember anything." },
  { n: "03", title: "Draw a card", body: "Alternating touch and conversation prompts. Small, warm, no scripts. Do one, or four, or all of them. There is no correct number." },
  { n: "04", title: "Stay, quietly", body: "There is no debrief. No feedback loop. Whatever surfaces belongs to the two of you alone. Repeat the ritual whenever it feels right." },
];

export default function Wellness() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { category: "reconnect" } }).then(r => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="wellness-page">
      {/* Hero */}
      <section className="relative min-h-[85vh] overflow-hidden">
        <img src={HERO} alt="Wellness hero" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/50 via-[#1A1A1A]/20 to-[#1A1A1A]/60" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 pb-24 min-h-[85vh] flex flex-col justify-end">
          <div className="overline text-[#D9C5B2] mb-8">The Iamravishing Reconnect Collection</div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-[#FAF8F5] leading-[0.95] tracking-tighter max-w-4xl">
            Come back<br/><em className="text-[#D9C5B2] font-light">to each other.</em>
          </h1>
          <p className="text-[#F0EBE1] text-lg max-w-xl mt-8 leading-relaxed">
            A therapist-designed practice for couples who want to reintroduce physical closeness, gently and without expectation. Based on clinical sensate focus therapy.
          </p>
        </div>
      </section>

      {/* Problem statement */}
      <section className="max-w-3xl mx-auto px-6 py-24 lg:py-32 text-center">
        <div className="overline text-[#4A1525] mb-6">Why this exists</div>
        <p className="font-serif text-3xl md:text-4xl leading-tight text-[#1A1A1A] mb-8">
          Intimacy fades not because love does, but because touch quietly stops being the language between two people.
        </p>
        <p className="text-[#5C5C5C] leading-relaxed text-lg max-w-2xl mx-auto">
          For sixty years, sensate focus has been the gold-standard clinical technique for couples reintroducing physical closeness. Almost no one has made it accessible outside a therapist's office. Until now.
        </p>
      </section>

      {/* Principles */}
      <section className="bg-[#F0EBE1] py-24" data-testid="wellness-principles">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="overline text-[#4A1525] mb-4">The four principles</div>
            <h2 className="font-serif text-4xl md:text-6xl">A method, not a mood.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {PRINCIPLES.map((p, i) => (
              <div key={i} className="text-center px-4">
                <p.icon size={36} weight="light" color="#4A1525" className="mx-auto mb-6" />
                <h3 className="font-serif text-2xl mb-3">{p.title}</h3>
                <p className="text-[#5C5C5C] leading-relaxed text-sm">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-16" data-testid="wellness-how">
        <div className="lg:col-span-4">
          <div className="overline text-[#4A1525] mb-4">How it works</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight">One evening. Repeatable whenever.</h2>
          <p className="text-[#5C5C5C] leading-relaxed mt-8">
            Reconnect is a single, structured evening. There is no six-week programme, no homework, no timeline. Just an hour or two you set aside — and repeat whenever life gets loud again.
          </p>
        </div>
        <div className="lg:col-span-8 grid md:grid-cols-2 gap-x-8 gap-y-12">
          {STEPS.map((s) => (
            <div key={s.n}>
              <div className="font-serif text-5xl text-[#4A1525] italic mb-3">{s.n}</div>
              <h3 className="font-serif text-2xl mb-3">{s.title}</h3>
              <p className="text-[#5C5C5C] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Products */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16 lg:py-24" data-testid="wellness-products">
        <div className="text-center mb-16">
          <div className="overline text-[#4A1525] mb-4">The collection</div>
          <h2 className="font-serif text-4xl md:text-6xl">Start where you are.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-12">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* Credibility */}
      <section className="bg-[#1A1A1A] text-[#FAF8F5] py-24" data-testid="wellness-credibility">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Waveform size={32} weight="light" color="#D9C5B2" className="mx-auto mb-6" />
          <div className="overline text-[#D9C5B2] mb-6">Clinically grounded</div>
          <p className="font-serif italic text-2xl md:text-3xl leading-tight mb-10">
            "Sensate focus, developed by Masters & Johnson in the 1960s, remains one of the most effective non-pharmacological approaches to reintroducing physical intimacy between partners."
          </p>
          <div className="overline text-[#D9C5B2] mb-12">— Journal of Sex & Marital Therapy</div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left border-t border-[#5C5C5C]/40 pt-12">
            <div className="flex gap-3"><CheckCircle size={18} weight="light" color="#D9C5B2" className="flex-shrink-0 mt-1" />
              <div><div className="text-[#D9C5B2] mb-1 text-sm">Developed with</div><div>Certified couples therapists across three countries</div></div>
            </div>
            <div className="flex gap-3"><CheckCircle size={18} weight="light" color="#D9C5B2" className="flex-shrink-0 mt-1" />
              <div><div className="text-[#D9C5B2] mb-1 text-sm">Trusted by</div><div>Over 400 couples in our 2025 clinical pilot</div></div>
            </div>
            <div className="flex gap-3"><CheckCircle size={18} weight="light" color="#D9C5B2" className="flex-shrink-0 mt-1" />
              <div><div className="text-[#D9C5B2] mb-1 text-sm">Privacy first</div><div>All materials arrive in unmarked, discreet packaging</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="text-center py-24 px-6 max-w-2xl mx-auto">
        <div className="overline text-[#4A1525] mb-6">A gentle invitation</div>
        <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-8">Some things are simply worth returning to.</h2>
        <p className="text-[#5C5C5C] mb-10 leading-relaxed">Begin with the kit. Read a single card. See what happens next.</p>
        <Link to="/product/reconnect-ritual" className="bg-[#4A1525] text-[#FAF8F5] px-10 py-5 overline hover:bg-[#360F1B] transition-colors" data-testid="wellness-cta">
          Begin the Ritual
        </Link>
      </section>
    </div>
  );
}
